import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../components/AppShell';
import { qbankApi, blueprintApi, parseTypes, QUESTION_TYPES } from '../lib/questionBankApi';
import { subjectLabel } from '../lib/questionBankApi';
import type {
  BankHealthView, BlueprintBody, BlueprintView, NamedOption,
} from '../lib/questionBankApi';

/**
 * THE PATTERN EDITOR — what a paper of this kind must look like.
 *
 * ⚠ THIS SCREEN IS THE ANSWER TO "CAN YOU CHANGE IT?". Every school asks for a different shape —
 * three MCQs or seven, at the top or at the end, five questions for a unit test and thirty-three
 * for a board paper — and until now the answer was "tell us and we'll change it for you". The
 * domain has always stored all of it as data; there has simply never been a screen. A pattern a
 * teacher can drag into shape at 9pm is worth more than any number of configurable back ends.
 *
 * ⚠ THE RUNNING TOTAL IS THE POINT OF THE LAYOUT, not decoration. Two constraint systems run at
 * right angles here: the sections, which a human can check by counting, and the two percentage
 * splits, which cut across every section and which nobody can check by eye. The bar at the top of
 * the editor shows both at once and never scrolls away.
 *
 * ⚠ AND THE TWO MISBEHAVE DIFFERENTLY ON SAVE. A marks mismatch is arithmetic in progress — you
 * type 70 and then build the sections that get there — so the server keeps it as a warning and so
 * do we. A split that does not add to 100 is a typo with a right answer and the server refuses it,
 * so this screen says so before the button is pressed rather than after.
 */

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/** ⚠ React keys, NOT anything the server sees. Index keys would re-use a row's DOM across a
    reorder, so the input you were typing in follows the row that took its place. */
let keySeed = 0;
const nextKey = () => `row-${++keySeed}`;

interface SectionDraft {
  key: string;
  label: string;
  title: string;
  instruction: string;
  questionCount: number;
  marksEach: number;
  internalChoiceCount: number;
  /** QuestionType names. Empty means "anything", which is what an empty CSV means to the server. */
  types: string[];
}

interface Draft {
  id: string | null;
  name: string;
  subjectId: string;
  classLabel: string;
  totalMarks: number;
  durationMinutes: number;
  generalInstructions: string;
  competencyPercent: number;
  objectivePercent: number;
  descriptivePercent: number;
  easyPercent: number;
  mediumPercent: number;
  hardPercent: number;
  freshnessDays: number;
  sections: SectionDraft[];
}

function draftFrom(v: BlueprintView): Draft {
  return {
    id: v.id,
    name: v.name,
    subjectId: v.subjectId ?? '',
    classLabel: v.classLabel ?? '',
    totalMarks: v.totalMarks,
    durationMinutes: v.durationMinutes,
    generalInstructions: v.generalInstructions ?? '',
    competencyPercent: v.competencyPercent,
    objectivePercent: v.objectivePercent,
    descriptivePercent: v.descriptivePercent,
    easyPercent: v.easyPercent,
    mediumPercent: v.mediumPercent,
    hardPercent: v.hardPercent,
    freshnessDays: v.freshnessDays,
    sections: [...v.sections]
      .sort((a, b) => a.sequence - b.sequence)
      .map((s) => ({
        key: nextKey(),
        label: s.label,
        title: s.title,
        instruction: s.instruction ?? '',
        questionCount: s.questionCount,
        marksEach: s.marksEach,
        internalChoiceCount: s.internalChoiceCount,
        types: parseTypes(s.allowedTypesCsv),
      })),
  };
}

/* A new pattern starts as the shape most schools are copying from — CBSE's own — because a blank
   form is a worse starting point than one wrong number. Every field is editable, which is the
   whole argument of the screen. */
function emptyDraft(): Draft {
  return {
    id: null,
    name: '',
    subjectId: '',
    classLabel: '',
    totalMarks: 80,
    durationMinutes: 180,
    generalInstructions: 'All questions are compulsory.',
    competencyPercent: 50,
    objectivePercent: 20,
    descriptivePercent: 30,
    easyPercent: 20,
    mediumPercent: 60,
    hardPercent: 20,
    freshnessDays: 365,
    sections: [{
      key: nextKey(),
      label: 'A',
      title: 'Objective type',
      instruction: 'All questions carry 1 mark each.',
      questionCount: 20,
      marksEach: 1,
      internalChoiceCount: 0,
      types: ['Mcq', 'AssertionReason'],
    }],
  };
}

function toBody(d: Draft, institutionId: string): BlueprintBody {
  return {
    institutionId,
    name: d.name.trim(),
    subjectId: d.subjectId || null,
    classLabel: d.classLabel.trim() || null,
    totalMarks: d.totalMarks,
    durationMinutes: d.durationMinutes,
    generalInstructions: d.generalInstructions.trim() || null,
    competencyPercent: d.competencyPercent,
    objectivePercent: d.objectivePercent,
    descriptivePercent: d.descriptivePercent,
    easyPercent: d.easyPercent,
    mediumPercent: d.mediumPercent,
    hardPercent: d.hardPercent,
    freshnessDays: d.freshnessDays,
    // ⚠ Sequence is the order on screen, 1..n. The server resequences anyway; sending the visible
    // order is what makes a drag mean something.
    sections: d.sections.map((s, i) => ({
      label: s.label.trim(),
      title: s.title.trim(),
      instruction: s.instruction.trim() || null,
      sequence: i + 1,
      questionCount: s.questionCount,
      marksEach: s.marksEach,
      internalChoiceCount: s.internalChoiceCount,
      allowedTypesCsv: s.types.join(','),
    })),
  };
}

/** A number field that may be emptied while typing. ⚠ `Number('')` is 0, not NaN, and a field that
    snaps to 0 the moment you clear it cannot be retyped without fighting it. */
function num(raw: string, fallback = 0): number {
  if (raw.trim() === '') return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

const typeLabel = (name: string) => QUESTION_TYPES.find((t) => t.name === name)?.label ?? name;

export function BlueprintEditorPage() {
  const { user } = useAuth();
  const [institutions, setInstitutions] = useState<NamedOption[]>([]);
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [subjects, setSubjects] = useState<NamedOption[]>([]);

  const [list, setList] = useState<BlueprintView[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [health, setHealth] = useState<BankHealthView | null>(null);
  const [checking, setChecking] = useState(false);

  /* ⚠ THE DRAGGED ROW IS A REF, NOT STATE. A drag reads it back inside dragover and drop, and
     React batches state updates — a fast drag (or a synthetic one in a test) can reach the drop
     before a setState has landed, and the drop then silently does nothing. The ref is the truth;
     the state below only drives the highlight. */
  const dragFromRef = useRef<number | null>(null);
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);

  /* ⚠ Measured once at mount rather than through a resize listener, because it only decides a
     DEFAULT. Fourteen type checkboxes are two rows on a laptop and seven on a phone — seven rows
     times five sections is a screen nobody can scroll past to reach the marks. So a phone opens
     with them summarised and a laptop with them showing, and either way the teacher can toggle. */
  const [wideScreen] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 700);
  const [openTypes, setOpenTypes] = useState<Record<string, boolean>>({});

  // Retiring the pattern the whole school's papers are cut from sits behind `hr` on the server.
  // Showing the button to a Teacher would only earn them a 403.
  const canRetire = !!user?.role && (ROLES.hr as readonly string[]).includes(user.role);

  // ---------------------------------------------------------------- bootstrap
  // The blueprint list has no institutions or subjects on it, so the question-bank list answers
  // for those — one row, purely for the two dropdowns. Same call the paper builder makes.
  useEffect(() => {
    let alive = true;
    qbankApi
      .list({ take: '1', ...(institutionId ? { institutionId } : {}) })
      .then((r) => {
        if (!alive) return;
        setInstitutions(r.institutions);
        setSubjects(r.subjects);
        if (!institutionId && r.institutionId) setInstitutionId(r.institutionId);
      })
      .catch(() => { /* the patterns below are the screen; the dropdowns are a convenience */ });
    return () => { alive = false; };
  }, [institutionId]);

  const reload = useCallback(() => {
    let alive = true;
    blueprintApi
      .list(institutionId)
      .then((r) => { if (alive) { setList(r); setFailed(false); } })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [institutionId]);

  useEffect(() => reload(), [reload]);

  const subjectName = useCallback(
    (id: string | null) => subjects.find((s) => s.id === id)?.name ?? null,
    [subjects],
  );

  // ---------------------------------------------------------------- the running total
  const totals = useMemo(() => {
    if (!draft) return null;
    const sectionMarks = draft.sections.reduce((t, s) => t + s.questionCount * s.marksEach, 0);
    const questions = draft.sections.reduce((t, s) => t + s.questionCount, 0);
    const choices = draft.sections.reduce((t, s) => t + s.internalChoiceCount, 0);
    const typology = draft.competencyPercent + draft.objectivePercent + draft.descriptivePercent;
    const difficulty = draft.easyPercent + draft.mediumPercent + draft.hardPercent;
    return {
      sectionMarks, questions, choices, typology, difficulty,
      marksGap: sectionMarks - draft.totalMarks,
      // The bank has to yield the choices too — an internal choice costs a question and earns no marks.
      demand: questions + choices,
    };
  }, [draft]);

  const splitsWrong = !!totals && (totals.typology !== 100 || totals.difficulty !== 100);

  // ---------------------------------------------------------------- editing
  const open = (v: BlueprintView) => {
    setDraft(draftFrom(v));
    setHealth(null);
    setError(null);
    setFlash(null);
    window.scrollTo({ top: 0 });
  };

  const openById = (id: string) => {
    setBusyId(id);
    blueprintApi.get(id)
      .then(open)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not open that pattern.'))
      .finally(() => setBusyId(null));
  };

  const startNew = () => {
    setDraft(emptyDraft());
    setHealth(null);
    setError(null);
    setFlash(null);
  };

  /* ⚠ Cloned with no name and opened straight away. The server names the copy "<name> (copy)" and
     the teacher renames it in the field that is already on screen — a school builds Unit Test,
     Monthly Test, Half-Yearly and Pre-Board once, each a variation of the last, and a naming
     dialog in front of that is a step for nothing. */
  const clone = (v: BlueprintView) => {
    setBusyId(v.id);
    setError(null);
    blueprintApi.clone(v.id)
      .then((r) => blueprintApi.get(r.id))
      .then((copy) => {
        open(copy);
        setFlash(`Copied. This is “${copy.name}” — rename it and change what differs.`);
        reload();
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not copy that pattern.'))
      .finally(() => setBusyId(null));
  };

  const retire = (v: BlueprintView) => {
    setBusyId(v.id);
    setError(null);
    blueprintApi.deactivate(v.id)
      .then(() => { setFlash(`“${v.name}” is retired. Papers already set from it are untouched.`); reload(); })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not retire that pattern.'))
      .finally(() => setBusyId(null));
  };

  const patch = (p: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const patchSection = (i: number, p: Partial<SectionDraft>) =>
    setDraft((d) => (d
      ? { ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, ...p } : s)) }
      : d));

  const addSection = () => setDraft((d) => {
    if (!d) return d;
    const label = LETTERS[d.sections.length] ?? '';
    return {
      ...d,
      sections: [...d.sections, {
        key: nextKey(),
        label,
        title: '',
        instruction: '',
        questionCount: 5,
        marksEach: 2,
        internalChoiceCount: 0,
        types: [],
      }],
    };
  });

  const removeSection = (i: number) =>
    setDraft((d) => (d ? { ...d, sections: d.sections.filter((_, j) => j !== i) } : d));

  /**
   * ⚠ THE ONE MOVE FUNCTION, AND BOTH INPUT METHODS GO THROUGH IT. Drag-and-drop is a mouse
   * gesture: HTML5 drag events do not fire from a finger, and half the people who will use this
   * are on a phone at home in the evening. The up/down buttons are therefore not an accessibility
   * afterthought — they are the only way this works on a touchscreen, and they are always visible.
   */
  const move = (from: number, to: number) => setDraft((d) => {
    if (!d || to < 0 || to >= d.sections.length || from === to) return d;
    const next = [...d.sections];
    const [row] = next.splice(from, 1);
    next.splice(to, 0, row);
    return { ...d, sections: next };
  });

  /* Labels are printed on the paper and the builder keys on them, so they are NOT rewritten on a
     drag — moving the MCQs to the end should not silently rename anything. But A, C, B down the
     page is nearly always an accident, so the screen offers the fix rather than making it. */
  const labelsOutOfOrder = !!draft && draft.sections.some((s, i) => s.label !== LETTERS[i]);

  const relabel = () => setDraft((d) => (d
    ? { ...d, sections: d.sections.map((s, i) => ({ ...s, label: LETTERS[i] ?? s.label })) }
    : d));

  const toggleType = (i: number, name: string) => setDraft((d) => {
    if (!d) return d;
    return {
      ...d,
      sections: d.sections.map((s, j) => {
        if (j !== i) return s;
        return s.types.includes(name)
          ? { ...s, types: s.types.filter((t) => t !== name) }
          : { ...s, types: [...s.types, name] };
      }),
    };
  });

  // ---------------------------------------------------------------- save
  /* ⚠ SAVE IS NEVER DISABLED, EVEN WHEN THE SPLIT IS WRONG. The warning above the button says what
     the server will say, so nobody is surprised; but a disabled Save in front of a section list
     somebody has spent ten minutes on is how the work gets abandoned instead of kept. If they
     press it anyway, the server's own sentence comes back verbatim — it is written for them. */
  const save = () => {
    if (!draft || !institutionId) return;
    setSaving(true);
    setError(null);
    setFlash(null);
    const body = toBody(draft, institutionId);
    const req = draft.id
      ? blueprintApi.update(draft.id, body).then(() => draft.id!)
      : blueprintApi.create(body).then((r) => r.id);

    req
      .then((id) => {
        setDraft((d) => (d ? { ...d, id } : d));
        setFlash('Saved.');
        reload();
        if (draft.subjectId) checkBank(id, draft.subjectId);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not save the pattern.'))
      .finally(() => setSaving(false));
  };

  /* The bank-health check the paper builder runs, run here instead — because "Section C cannot be
     filled" is worth knowing in the editor a fortnight before exam week, not in the builder during
     it. Same endpoint, same arithmetic. */
  const checkBank = (id: string, subjectId: string) => {
    setChecking(true);
    qbankApi.health(id, subjectId)
      .then(setHealth)
      .catch(() => setHealth(null))
      .finally(() => setChecking(false));
  };

  // ---------------------------------------------------------------- render
  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Opening the patterns…</p>;
  if (failed) {
    return (
      <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>
        Could not load the paper patterns.
      </div>
    );
  }

  return (
    <div className="qb bp">
      <header className="qb-head">
        <h1>Paper patterns</h1>
        <p className="qb-sub">
          The shape of a paper — how many sections, how many questions in each, what they are worth
          and what the board expects of the mix. Change it here and every paper built from it
          changes with it. Nobody has to ring us.
        </p>
      </header>

      {error && <div className="bp-error">{error}</div>}
      {flash && <div className="qb-flash">{flash}</div>}

      {!draft && (
        <>
          {/* One school, one button — no card wrapped around an empty row. The picker only earns
              its card when a trust has more than one campus to choose between. */}
          {institutions.length > 1 ? (
            <div className="card qb-controls">
              <label>
                <span>Institution</span>
                <select value={institutionId ?? ''} onChange={(e) => setInstitutionId(e.target.value)}>
                  {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </label>
              <div className="qb-actions">
                <button className="btn btn-primary" onClick={startNew}>New pattern</button>
              </div>
            </div>
          ) : (
            <div className="bp-toolbar">
              <button className="btn btn-primary" onClick={startNew}>New pattern</button>
            </div>
          )}

          {list.length === 0 ? (
            <div className="card" style={{ padding: 20 }}>
              <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: 'var(--ink-2)' }}>
                No patterns yet. Build the one you set most often — the board paper, usually — and
                copy it for the unit tests, which are the same shape with fewer questions.
              </p>
            </div>
          ) : (
            <div className="bp-list">
              {list.map((b) => {
                const gap = b.sectionMarks - b.totalMarks;
                return (
                  <div className="card bp-card" key={b.id}>
                    <div className="bp-card-main">
                      <h2>{b.name}</h2>
                      <p className="bp-card-meta">
                        {[subjectName(b.subjectId) ?? 'Any subject', b.classLabel, `${b.totalMarks} marks`,
                          `${b.durationMinutes} min`].filter(Boolean).join(' · ')}
                      </p>
                      <p className="bp-card-shape">
                        {b.sections.length === 0
                          ? 'No sections yet'
                          : [...b.sections].sort((x, y) => x.sequence - y.sequence)
                            .map((s) => `${s.label} ${s.questionCount}×${s.marksEach}`).join('  ·  ')}
                      </p>
                      <div className="bp-card-flags">
                        {gap !== 0 && (
                          <span className="qb-chip bad">
                            sections add to {b.sectionMarks}, not {b.totalMarks}
                          </span>
                        )}
                        {!b.typologyAddsUp && (
                          <span className="qb-chip bad">typology split is not 100%</span>
                        )}
                      </div>
                    </div>
                    <div className="bp-card-actions">
                      <button className="btn btn-primary" disabled={busyId !== null}
                        onClick={() => openById(b.id)}>
                        {busyId === b.id ? 'Opening…' : 'Edit'}
                      </button>
                      <button className="btn" disabled={busyId !== null} onClick={() => clone(b)}>
                        Duplicate
                      </button>
                      {canRetire && (
                        <button className="btn" disabled={busyId !== null} onClick={() => retire(b)}>
                          Retire
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {draft && totals && (
        <>
          <div className="bp-crumb">
            <button className="btn" onClick={() => { setDraft(null); setHealth(null); setFlash(null); setError(null); }}>
              ← All patterns
            </button>
            <span className="bp-crumb-name">{draft.name || 'New pattern'}</span>
          </div>

          {/* ⚠ STICKY, AND THAT IS THE FEATURE. The section list is long enough to scroll on any
              screen; a total you have to scroll back up to read is a total nobody reads. */}
          <div className="bp-totals" role="status" aria-live="polite">
            <div className={`bp-total ${totals.marksGap === 0 ? 'is-ok' : 'is-warn'}`}>
              <span className="bp-total-value">{totals.sectionMarks} / {draft.totalMarks}</span>
              <span className="bp-total-label">
                {totals.marksGap === 0 ? 'marks — the sections add up'
                  : totals.marksGap > 0 ? `marks — ${totals.marksGap} over the total`
                    : `marks — ${-totals.marksGap} short of the total`}
              </span>
            </div>
            <div className={`bp-total ${totals.typology === 100 ? 'is-ok' : 'is-bad'}`}>
              <span className="bp-total-value">{totals.typology}%</span>
              <span className="bp-total-label">
                typology split{totals.typology === 100 ? '' : ' — must be 100'}
              </span>
            </div>
            <div className={`bp-total ${totals.difficulty === 100 ? 'is-ok' : 'is-bad'}`}>
              <span className="bp-total-value">{totals.difficulty}%</span>
              <span className="bp-total-label">
                difficulty mix{totals.difficulty === 100 ? '' : ' — must be 100'}
              </span>
            </div>
            <div className="bp-total">
              <span className="bp-total-value">{totals.questions}</span>
              <span className="bp-total-label">
                questions{totals.choices > 0 ? ` · ${totals.demand} needed with the choices` : ''}
              </span>
            </div>
          </div>

          {splitsWrong && (
            <div className="bp-error">
              A split has to add up to 100%. The typology split is at {totals.typology}% and the
              difficulty mix at {totals.difficulty}% — the server refuses a save until both are 100.
            </div>
          )}
          {totals.marksGap !== 0 && (
            <div className="qb-note">
              The sections come to {totals.sectionMarks} marks and the paper says {draft.totalMarks}.
              That is usually one section with a typo in it — but it does not stop you saving, because
              a pattern is built one section at a time.
            </div>
          )}

          <div className="bp-editor">
            {/* ------------------------------------------------------- header fields */}
            <div className="card bp-head-fields">
              <h2>The paper</h2>

              <label className="bp-field">
                <span>Name</span>
                <input value={draft.name} maxLength={160} placeholder="Unit Test 1 — Physics XII"
                  onChange={(e) => patch({ name: e.target.value })} />
              </label>

              <label className="bp-field">
                <span>Subject</span>
                <select value={draft.subjectId} onChange={(e) => patch({ subjectId: e.target.value })}>
                  <option value="">Any subject</option>
                  {/* ⚠ Code shown only where names collide — see subjectLabel. */}
                  {subjects.map((s) => <option key={s.id} value={s.id}>{subjectLabel(s, subjects)}</option>)}
                </select>
              </label>

              <div className="bp-pair">
                <label className="bp-field">
                  <span>Class</span>
                  <input value={draft.classLabel} placeholder="XII"
                    onChange={(e) => patch({ classLabel: e.target.value })} />
                </label>
                <label className="bp-field">
                  <span>Total marks</span>
                  <input type="number" inputMode="numeric" min={1} max={1000} value={draft.totalMarks}
                    onChange={(e) => patch({ totalMarks: num(e.target.value) })} />
                </label>
              </div>

              <div className="bp-pair">
                <label className="bp-field">
                  <span>Duration (minutes)</span>
                  <input type="number" inputMode="numeric" min={5} max={600} value={draft.durationMinutes}
                    onChange={(e) => patch({ durationMinutes: num(e.target.value) })} />
                </label>
                <label className="bp-field">
                  <span>Freshness (days)</span>
                  <input type="number" inputMode="numeric" min={0} max={3650} value={draft.freshnessDays}
                    onChange={(e) => patch({ freshnessDays: num(e.target.value) })} />
                </label>
              </div>
              <p className="bp-hint">
                A question used inside the freshness window is avoided, not forbidden — a small bank
                cannot fill three papers a term without repeating, and the builder says when it did.
              </p>

              <label className="bp-field">
                <span>General instructions</span>
                <textarea rows={4} value={draft.generalInstructions}
                  placeholder="Printed above Section A."
                  onChange={(e) => patch({ generalInstructions: e.target.value })} />
              </label>

              <div className="bp-split">
                <h3>Typology split</h3>
                <p className="bp-hint">
                  What the questions test, not what shape they are. CBSE's rule for Classes 10 and 12
                  is 50 / 20 / 30, and it cuts across every section.
                </p>
                <div className="bp-triple">
                  <label className="bp-field">
                    <span>Competency</span>
                    <input type="number" inputMode="numeric" min={0} max={100} value={draft.competencyPercent}
                      onChange={(e) => patch({ competencyPercent: num(e.target.value) })} />
                  </label>
                  <label className="bp-field">
                    <span>Objective</span>
                    <input type="number" inputMode="numeric" min={0} max={100} value={draft.objectivePercent}
                      onChange={(e) => patch({ objectivePercent: num(e.target.value) })} />
                  </label>
                  <label className="bp-field">
                    <span>Descriptive</span>
                    <input type="number" inputMode="numeric" min={0} max={100} value={draft.descriptivePercent}
                      onChange={(e) => patch({ descriptivePercent: num(e.target.value) })} />
                  </label>
                </div>
                <p className={`bp-sum ${totals.typology === 100 ? 'tone-ok' : 'tone-bad'}`}>
                  {draft.competencyPercent} + {draft.objectivePercent} + {draft.descriptivePercent}
                  {' = '}{totals.typology}%
                  {totals.typology === 100 ? '' : ' — the server will refuse this'}
                </p>
              </div>

              <div className="bp-split">
                <h3>Difficulty mix</h3>
                <p className="bp-hint">Not a board rule. A good head of department's rule.</p>
                <div className="bp-triple">
                  <label className="bp-field">
                    <span>Easy</span>
                    <input type="number" inputMode="numeric" min={0} max={100} value={draft.easyPercent}
                      onChange={(e) => patch({ easyPercent: num(e.target.value) })} />
                  </label>
                  <label className="bp-field">
                    <span>Medium</span>
                    <input type="number" inputMode="numeric" min={0} max={100} value={draft.mediumPercent}
                      onChange={(e) => patch({ mediumPercent: num(e.target.value) })} />
                  </label>
                  <label className="bp-field">
                    <span>Hard</span>
                    <input type="number" inputMode="numeric" min={0} max={100} value={draft.hardPercent}
                      onChange={(e) => patch({ hardPercent: num(e.target.value) })} />
                  </label>
                </div>
                <p className={`bp-sum ${totals.difficulty === 100 ? 'tone-ok' : 'tone-bad'}`}>
                  {draft.easyPercent} + {draft.mediumPercent} + {draft.hardPercent}
                  {' = '}{totals.difficulty}%
                  {totals.difficulty === 100 ? '' : ' — the server will refuse this'}
                </p>
              </div>
            </div>

            {/* ------------------------------------------------------- the sections */}
            <div className="bp-sections">
              <div className="bp-sections-head">
                <h2>Sections</h2>
                <p className="bp-hint">
                  Drag a section by its handle, or use the arrows — the order here is the order on
                  the printed paper, so this is how the multiple choice moves to the end.
                </p>
                {labelsOutOfOrder && (
                  <p className="bp-hint">
                    The labels no longer run A, B, C down the page. They are printed as they stand —
                    <button className="bp-linkbtn" onClick={relabel}>relabel them in order</button>
                    if that was not deliberate.
                  </p>
                )}
              </div>

              {draft.sections.map((s, i) => (
                <div
                  key={s.key}
                  className={`card bp-section ${dragOver === i && dragFrom !== null && dragFrom !== i ? 'is-over' : ''} ${dragFrom === i ? 'is-dragging' : ''}`}
                  onDragOver={(e) => { if (dragFromRef.current !== null) { e.preventDefault(); setDragOver(i); } }}
                  onDrop={(e) => {
                    const from = dragFromRef.current;
                    if (from === null) return;
                    e.preventDefault();
                    move(from, i);
                    dragFromRef.current = null;
                    setDragFrom(null);
                    setDragOver(null);
                  }}
                >
                  <div className="bp-section-bar">
                    {/* ⚠ draggable sits on the HANDLE, not on the card. A draggable element that
                        contains text inputs eats the caret and text selection inside them. */}
                    <span
                      className="bp-grip"
                      draggable
                      aria-hidden="true"
                      onDragStart={(e) => {
                        dragFromRef.current = i;
                        setDragFrom(i);
                        e.dataTransfer.effectAllowed = 'move';
                        // ⚠ setData IS NOT OPTIONAL. A dragstart that leaves the dataTransfer empty
                        // is cancelled outright by Chromium and never starts in Firefox — no
                        // dragover, no drop, a handle that looks draggable and does nothing. Found
                        // exactly that way: the buttons worked and the drag silently did not.
                        e.dataTransfer.setData('text/plain', String(i));
                      }}
                      onDragEnd={() => { dragFromRef.current = null; setDragFrom(null); setDragOver(null); }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
                        <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
                        <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
                      </svg>
                    </span>

                    <span className="bp-section-name">
                      Section {s.label || '—'}
                      <span className="bp-section-sum">
                        {' '}· {plural(s.questionCount, 'question')} × {s.marksEach}
                        {' = '}{s.questionCount * s.marksEach} marks
                        {s.internalChoiceCount > 0 && ` · ${s.internalChoiceCount} with a choice`}
                      </span>
                    </span>

                    <div className="bp-move">
                      <button className="bp-iconbtn" onClick={() => move(i, i - 1)} disabled={i === 0}
                        aria-label={`Move section ${s.label || i + 1} up`} title="Move up">↑</button>
                      <button className="bp-iconbtn" onClick={() => move(i, i + 1)}
                        disabled={i === draft.sections.length - 1}
                        aria-label={`Move section ${s.label || i + 1} down`} title="Move down">↓</button>
                      <button className="bp-iconbtn is-danger" onClick={() => removeSection(i)}
                        aria-label={`Delete section ${s.label || i + 1}`} title="Delete section">×</button>
                    </div>
                  </div>

                  <div className="bp-section-grid">
                    <label className="bp-field bp-narrow">
                      <span>Label</span>
                      <input value={s.label} maxLength={4}
                        onChange={(e) => patchSection(i, { label: e.target.value })} />
                    </label>
                    <label className="bp-field bp-grow">
                      <span>Title</span>
                      <input value={s.title} placeholder="Short answer"
                        onChange={(e) => patchSection(i, { title: e.target.value })} />
                    </label>
                    <label className="bp-field bp-narrow">
                      <span>Questions</span>
                      <input type="number" inputMode="numeric" min={1} max={200} value={s.questionCount}
                        onChange={(e) => patchSection(i, { questionCount: num(e.target.value) })} />
                    </label>
                    <label className="bp-field bp-narrow">
                      <span>Marks each</span>
                      <input type="number" inputMode="numeric" min={1} max={100} value={s.marksEach}
                        onChange={(e) => patchSection(i, { marksEach: num(e.target.value) })} />
                    </label>
                    <label className="bp-field bp-narrow">
                      <span>Internal choice</span>
                      <input type="number" inputMode="numeric" min={0} max={s.questionCount}
                        value={s.internalChoiceCount}
                        onChange={(e) => patchSection(i, { internalChoiceCount: num(e.target.value) })} />
                    </label>
                  </div>

                  {s.internalChoiceCount > s.questionCount && (
                    <p className="bp-sum tone-bad">
                      Section {s.label || i + 1} offers a choice on {s.internalChoiceCount} questions
                      but only has {s.questionCount}. The server refuses that.
                    </p>
                  )}

                  <label className="bp-field">
                    <span>Printed instruction</span>
                    <input value={s.instruction}
                      placeholder="Each question carries 3 marks. Answer in about 60 words."
                      onChange={(e) => patchSection(i, { instruction: e.target.value })} />
                  </label>

                  {(() => {
                    const shown = openTypes[s.key] ?? wideScreen;
                    return (
                      <div className="bp-types" role="group" aria-label={`Question types allowed in section ${s.label || i + 1}`}>
                        <button className="bp-types-toggle" aria-expanded={shown}
                          onClick={() => setOpenTypes((o) => ({ ...o, [s.key]: !shown }))}>
                          <span className="bp-types-caption">Question types allowed</span>
                          <span className="bp-types-summary">
                            {s.types.length === 0
                              ? 'nothing ticked — anything may go here'
                              : s.types.map(typeLabel).join(', ')}
                          </span>
                          <span aria-hidden="true">{shown ? '▲' : '▼'}</span>
                        </button>
                        {shown && (
                          <div className="bp-type-grid">
                            {QUESTION_TYPES.map((t) => (
                              <label key={t.name} className={`bp-type ${s.types.includes(t.name) ? 'is-on' : ''}`}>
                                <input type="checkbox" checked={s.types.includes(t.name)}
                                  onChange={() => toggleType(i, t.name)} />
                                {t.label}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}

              <div className="bp-section-add">
                <button className="btn" onClick={addSection}>Add a section</button>
              </div>

              <div className="card bp-save">
                <p className="bp-hint">
                  Saving replaces the whole section list with what is on screen — the order
                  included. Nothing else is touched.
                </p>
                {splitsWrong && (
                  <p className="bp-sum tone-bad">
                    The splits do not add to 100 — save will come back with exactly that.
                  </p>
                )}
                <div className="qb-actions">
                  <button className="btn btn-primary" onClick={save} disabled={saving || !institutionId}>
                    {saving ? 'Saving…' : draft.id ? 'Save the pattern' : 'Create the pattern'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* --------------------------------------------------------- bank health */}
          {draft.id && (
            <div className="card bp-check">
              <h2>Can the bank fill this?</h2>
              <p className="bp-hint">
                The same check the paper builder runs, run here — so a section the bank cannot fill
                is something you find out a fortnight before exam week rather than during it.
              </p>
              {!draft.subjectId && (
                <p className="bp-hint">Pick a subject above and the check has something to count.</p>
              )}
              <div className="qb-actions">
                <button className="btn" disabled={!draft.subjectId || checking}
                  onClick={() => draft.subjectId && checkBank(draft.id!, draft.subjectId)}>
                  {checking ? 'Counting…' : 'Check the bank'}
                </button>
                <Link className="btn" to="/paper-builder">Open the paper builder</Link>
              </div>

              {health && (
                <div className={`qb-health verdict-${health.verdict.toLowerCase()}`} style={{ padding: 0 }}>
                  <div className="qb-health-head">
                    <span className={`qb-verdict v-${health.verdict.toLowerCase()}`}>
                      {health.verdict === 'Ready' ? 'Ready'
                        : health.verdict === 'Thin' ? 'Thin' : 'Cannot build'}
                    </span>
                    <p>{health.headline}</p>
                  </div>
                  <div className="qb-supply">
                    <table className="qb-table">
                      <thead>
                        <tr><th>Section</th><th className="num">Needs</th><th className="num">Approved</th>
                          <th className="num">Draft</th><th>Verdict</th></tr>
                      </thead>
                      <tbody>
                        {health.sections.map((s) => (
                          <tr key={s.label} className={s.shortfall > 0 ? 'is-short' : ''}>
                            <td><strong>{s.label}</strong> · {s.title}</td>
                            <td className="num">{s.demand}</td>
                            <td className="num">{s.approved}</td>
                            <td className="num">{s.draft || '—'}</td>
                            <td>
                              {s.shortfall === 0
                                ? <span className="tone-ok">covered</span>
                                : s.draftsWouldFix
                                  ? <span className="tone-warn">short {s.shortfall} — the drafts would fix it</span>
                                  : <span className="tone-bad">short {s.shortfall}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
